/**
 * MembershipCard.js — Thomas Fogarty wine-club membership card (cardType "membership").
 *
 * Fogarty override of the base MembershipCard. The old Fogarty card is
 * intentionally SIMPLE: cover image, uppercase serif title, the
 * detailed_description, a "Learn more" button, and a "Join Now" CTA pinned
 * bottom-right. It does NOT render the membership type ("Membership"),
 * bottles/curated-by/shipping meta lines, or a "x% Member Discount" price — the
 * base card shows all of those, which is exactly why Fogarty needs this override
 * to match the old.
 *
 * DOM parity with old createWineMembership (.card-box):
 *   cover → title → inner (desc, Learn more, Join Now footer)
 *
 * LEARN MORE: emits ws-prompt-select with the card's `message` (old cardClicked
 * -> sendMessage), echoed as a user turn for the detailed view.
 *
 * JOIN NOW: always rendered (old createWineMembership never gated on
 * display_button / cta_link) — solid maroon CTA bottom-right.
 *
 * Reference: RedConcierge/static/javascript/main.js createWineMembership() +
 *   index.css .card-box-title / .learn-more / .innerfooter.
 */

import { emit ,isChatDisabled} from "../../../core/state.js";
import { ASSETS_BASE_URL } from "../../../core/env.js";

// Production membership booklet icon, including the person silhouette.
const LEARN_ICON = "<svg aria-hidden=\"true\" xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" viewBox=\"0 0 16 16\" fill=\"none\"> <path d=\"M2.66675 13.0002V3.00016C2.66675 2.55814 2.84234 2.13421 3.1549 1.82165C3.46746 1.50909 3.89139 1.3335 4.33341 1.3335H13.3334V14.6668H4.33341C3.89139 14.6668 3.46746 14.4912 3.1549 14.1787C2.84234 13.8661 2.66675 13.4422 2.66675 13.0002ZM2.66675 13.0002C2.66675 12.5581 2.84234 12.1342 3.1549 11.8217C3.46746 11.5091 3.89139 11.3335 4.33341 11.3335H13.3334M8.00008 6.66683C8.73646 6.66683 9.33341 6.06988 9.33341 5.3335C9.33341 4.59712 8.73646 4.00016 8.00008 4.00016C7.2637 4.00016 6.66675 4.59712 6.66675 5.3335C6.66675 6.06988 7.2637 6.66683 8.00008 6.66683ZM8.00008 6.66683C8.53051 6.66683 9.03922 6.87754 9.4143 7.25262C9.78937 7.62769 10.0001 8.1364 10.0001 8.66683M8.00008 6.66683C7.46965 6.66683 6.96094 6.87754 6.58587 7.25262C6.21079 7.62769 6.00008 8.1364 6.00008 8.66683\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> </svg>";

export default class MembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card";

    const openDetail = () => {
      if(isChatDisabled()) return;
      const label =
        club.message || `Tell me more about ${club.title || "this membership"}`;
      emit("ws-prompt-select", {
        label,
        value: label,
        uniqueId: club.unique_id,
      });
    };

    // Cover image (clickable → detail). Old fell back to the padded house logo
    // when no cover image was set.
    const head = document.createElement("div");
    head.className = "ws-card-head";
    const img = document.createElement("img");
    img.className = "ws-card-img";
    img.loading = "lazy";
    img.alt = club.title || "Membership";
    img.style.cursor = "pointer";
    img.addEventListener("click", openDetail);
    if (club.cover_image_link) {
      img.src = club.cover_image_link;
    } else {
      img.src = `${ASSETS_BASE_URL}/assets/fogartywinery/header-logo.png`;
      img.classList.add("ws-fg-mem-logo");
    }
    head.appendChild(img);
    card.appendChild(head);

    // Title sits between cover and inner panel (old .card-box-title).
    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = String(club.title).replace(/<br\s*\/?>/gi, " ");
      // Old rendered the title via innerHTML, so an admin-entered <br> in the
      // title (e.g. "Six Bottle<br>Selection") became a line break. textContent
      // would show it literally, so convert <br> → newline and let CSS
      // (white-space: pre-line) render the break — without injecting raw HTML.
      title.textContent = String(club.title).replace(/<br\s*\/?>/gi, "\n");
      card.appendChild(title);
    }

    // Old .innercardbox — desc + Learn more + Join Now footer.
    const body = document.createElement("div");
    body.className = "ws-card-body ws-fg-mem-body";

    const descRaw = club.detailed_description || club.description;
    const descText = Array.isArray(descRaw) ? descRaw.join(" ") : descRaw;
    if (descText) {
      const desc = document.createElement("p");
      desc.className = "ws-card-desc-full";
      desc.textContent = descText;
      body.appendChild(desc);
    }

    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${LEARN_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    body.appendChild(learn);

    // Footer: Join Now always bottom-right (old .innerfooter — always emitted
    // with cta_text / cta_link; never gated on display_button).
    const footer = document.createElement("div");
    footer.className = "ws-fg-mem-footer";
    const cta = document.createElement("a");
    cta.className = "ws-card-cta ws-fg-mem-cta";
    if (club.cta_link) {
      cta.href = club.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
    }
    cta.textContent = club.cta_text || "Join Now";
    footer.appendChild(cta);
    body.appendChild(footer);

    card.appendChild(body);
    return card;
  }
}
